import classNames from "classnames";
import React, { useEffect, useState } from "react";
import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
} from "react-router-dom";

import styles from "./App.module.scss";
import { ErrorWrapper, LoadingPage, Navbar, ReadOnlyPlay } from "./components";
import UpgradeConsumerModal from "./components/UpgradeConsumerModal/upgrade-consumer-modal";
import { LoginFailedScreen } from "./components/login-failed-screen/login-failed-screen";
import { NoProductScreen } from "./components/no-product-screen/no-product-screen";
import { useLogin, useLogout, useSetCourtOptions, useSetSport, useSetTheme } from "./hooks";
import { useCheckProducts } from "./hooks/use-check-products";
import { useSetAppcues } from "./hooks/use-set-appcues";
import { Draw, Fasttrade, Home, ManageFields, Play, Playbooks, Redirect, Tab } from "./pages";
import { FAST_SERVER_URL } from "./redux/constants";
import { useAppSelector } from "./redux/hooks";
import { useGetUser } from "./redux/slices";
import { RootState } from "./redux/store";
import { getClientSecretBasicAuthHeader } from "./redux/utils";
import "./sass/main.scss";

declare global {
  interface Window {
    recharge: any;
  }
}

export const App = () => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<AppRoot />}>
        <Route index element={<Navigate to="home" replace />} />
        <Route path="login" element={<Redirect />} />
        <Route path="fasttrade" element={<Fasttrade />} />
        <Route path="plays/:playId" element={<Play />} />
        <Route path="plays/readonly/:playId" element={<ReadOnlyPlay />} />
        <Route path="plays/readonly/:playId/:frameIndex" element={<ReadOnlyPlay />} />
        <Route path="plays/readonly-interactive/:playId" element={<ReadOnlyPlay interactive />} />
        <Route path="plays/readonly-interactive/:playId/:frameIndex" element={<ReadOnlyPlay interactive />} />
        <Route path="home/*" element={<Home />}>
          <Route path={Tab.DRAW} element={<Draw />} />
          <Route path={`${Tab.PLAYBOOKS}/:playbookId?`} element={<Playbooks />} />
          <Route path={Tab.FIELDS} element={<ManageFields />} />
          <Route path={"*"} element={<Navigate to={Tab.DRAW} replace />} />
        </Route>
      </Route>
    )
  );

  return <RouterProvider router={router} fallbackElement={<LoadingPage />} />;
};

const AppRoot = () => {
  const { allowNoAccessScreen, loginCodeFailed } = useLogin();
  const auth = useAppSelector((state) => state.auth);
  const { loading } = auth;
  const { hasFastDraw, loading: isLoadingProducts } = useCheckProducts();
  useSetTheme();
  const { isLoadingSport } = useSetSport();
  const { user, error: getUserError } = useGetUser();
  const userDetails = user?.userDetails;
  const accountType = userDetails?.AccountType;
  useSetCourtOptions();
  useSetAppcues();

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [payload, setPayload] = useState(null);
  const [customerPortalUrl, setCustomerPortalUrl] = useState("");
  const [logoutCalled, setLogoutCalled] = useState(false);
  const { logout } = useLogout(auth.token);

  // const TEMP_SERVER = "https://backend.fastmodelsports.com";
  useEffect(() => {
    if (accountType === "consumer" && !showUpgradeModal) {
      const { token } = auth;
      const recharge = window.recharge;

      recharge.init({
        storeIdentifier: "fastmodel-sports.myshopify.com",
        storefrontAccessToken: "strfnt_ef5b237464669df5241579c884806b83ce9db2d808bc0931546e060f66c88d8e",
      });

      const checkAndOpenModal = async () => {
        try {
          const eligibilityResponse = await fetch(`${FAST_SERVER_URL}recharge/upgrade/eligible`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (!eligibilityResponse.ok) {
            throw new Error(`Error checking upgrade eligibility: ${eligibilityResponse.statusText}`);
          }
          const isUpgradable = await eligibilityResponse.json();

          if (isUpgradable) {
            // Fetch the recharge upgrade payload
            const payloadResponse = await fetch(`${FAST_SERVER_URL}recharge/upgrade/link`, {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            });

            if (!payloadResponse.ok) {
              throw new Error(`Error fetching upgrade payload: ${payloadResponse.statusText}`);
            }

            const payloadData = await payloadResponse.json();
            setPayload(payloadData);

            recharge.customer.getCustomerPortalAccess(payloadData.session).then((rechargeRes: any) => {
              setCustomerPortalUrl(rechargeRes.portal_url);
              setShowUpgradeModal(true);
            });
          }
        } catch (error) {
          console.error("Error during upgrade check:", error);
          // Handle the error appropriately
        }
      };
      checkAndOpenModal();
    }
  }, [accountType, showUpgradeModal, auth.token]);

  // check for readonly in the url
  const location = useLocation();
  const readOnly = location.pathname.includes("readonly");

  const isAccessedViaIframe = window.self !== window.top;

  if (getUserError) {
    if (!logoutCalled) {
      logout();
      setLogoutCalled(true);
    }
    return <LoadingPage className={styles.loadingPage} />;
  }

  if (!hasFastDraw && !isLoadingProducts) {
    if (loginCodeFailed) {
      return <LoginFailedScreen />;
    } else if (allowNoAccessScreen) {
      return <NoProductScreen />;
    } else {
      return <LoadingPage className={styles.loadingPage} />;
    }
  }

  const upgradeSubscription = (sku: string, externalVariantId: string): Promise<any> => {
    const accessToken = auth.token; // Ensure you have the access token
    if (!accessToken) {
      return Promise.reject(new Error("Access token is not available"));
    }

    const url = `${FAST_SERVER_URL}recharge/upgrade/subscription?sku=${sku}&externalVariantId=${externalVariantId}`;

    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      if (!response.ok) {
        return response.json().then((errorData) => {
          throw new Error(errorData.message || "Error upgrading subscription");
        });
      }
      return response.json();
    });
  };

  return (
    <ErrorWrapper>
      <div
        className={classNames(styles.app, {
          [styles.readOnly || ""]: readOnly,
          [styles.iframe || ""]: isAccessedViaIframe,
        })}
      >
        {!readOnly && !isAccessedViaIframe && <Navbar />}
        {loading || isLoadingSport || isLoadingProducts ? <LoadingPage className={styles.loadingPage} /> : <Outlet />}
      </div>
      <UpgradeConsumerModal
        open={showUpgradeModal}
        payload={payload}
        upgradeLink={customerPortalUrl}
        onClose={() => setShowUpgradeModal(false)}
        upgradePromise={upgradeSubscription} // Pass the function as a prop
        logout={logout}
      />{" "}
    </ErrorWrapper>
  );
};
